import { useAuth } from "react-oidc-context";

import { config, redirectUri } from "./config";
import { useRoute } from "./router";
import { Inbox } from "./pages/Inbox";
import { CleanersSection, ContractsSection } from "./pages/Sections";
import { Website } from "./pages/Website";
import { Nav } from "./components/Nav";

export function App() {
  const auth = useAuth();
  const { path } = useRoute();

  // /cleaners/leads -> section "cleaners", tab "leads". A bare section is its
  // first tab.
  const [, first = "", second = ""] = path.split("/");
  const section = first === "cleaners" || first === "contracts" ? first : "";
  const tab = ["leads", "ads"].includes(second) ? second : "";
  const websiteTab = ["copy", "design"].includes(second) ? second : "";

  if (auth.isLoading) {
    return <Splash>Checking your session…</Splash>;
  }

  if (auth.error) {
    return (
      <Splash>
        <p className="splash__error">Sign-in failed: {auth.error.message}</p>
        <button className="btn" onClick={() => void auth.signinRedirect()}>
          Try again
        </button>
      </Splash>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Splash>
        <h1>Hunt's Cleaning</h1>
        <p>Admin access is limited to staff accounts.</p>
        <button className="btn btn--primary" onClick={() => void auth.signinRedirect()}>
          Sign in
        </button>
      </Splash>
    );
  }

  const signOut = () => {
    // Cognito's logout endpoint isn't part of OIDC discovery, so oidc-client-ts
    // can't build this URL itself. Clearing local state first means a failed
    // redirect still leaves the app signed out locally.
    void auth.removeUser().then(() => {
      const url = new URL(`${config.hostedUi}/logout`);
      url.searchParams.set("client_id", config.clientId);
      url.searchParams.set("logout_uri", redirectUri);
      window.location.href = url.toString();
    });
  };

  return (
    <div className="shell">
      <Nav path={path} email={auth.user?.profile.email} onSignOut={signOut} />
      <main className="content">
        {path === "/" && <Inbox />}
        {first === "website" && <Website tab={websiteTab} />}
        {section === "cleaners" && <CleanersSection tab={tab} />}
        {section === "contracts" && <ContractsSection tab={tab} />}
        {path !== "/" && first !== "website" && !section && (
          <div className="empty">
            <h2>Page not found</h2>
            <p>
              <a href="/">Back to the inbox</a>
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <div className="splash">
      <div className="splash__card">{children}</div>
    </div>
  );
}
